import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useContext } from "react";
import axios from "axios";
import Header from "../../../components/Header";
import { AuthContext } from "../../utils/AuthContext";
import toast from "react-hot-toast";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import {
  Calendar, Moon, ShieldCheck, ShieldX, Shield,
  MapPin, Users, ChevronLeft, Check, Info
} from "lucide-react";

const G      = "#3A9D23";
const G_DARK = "#2f7d1c";
const API    = "https://api.komorotel.com";

const KMF_PER_EUR = 491.96775;
const toKMF = (eur) => eur ? Math.round(eur * KMF_PER_EUR).toLocaleString("fr-FR") : null;

const fmtDate  = d => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long",  year: "numeric" });
const fmtShort = d => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });

export default function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const params   = new URLSearchParams(location.search);

  const roomId     = params.get("room");
  const checkIn    = params.get("checkIn");
  const checkOut   = params.get("checkOut");
  const ratePlanId = params.get("ratePlanId");
  // ✅ adults/children depuis l'URL
  const adults     = parseInt(params.get("adults"))   || 1;
  const children   = parseInt(params.get("children")) || 0;

  const [room,           setRoom]           = useState(null);
  const [ratePlan,       setRatePlan]       = useState(null);
  const [total,          setTotal]          = useState(null);
  const [totalKMF,       setTotalKMF]       = useState(null);
  const [pricePerNight,  setPricePerNight]  = useState(null);
  const [pricePerNightKMF, setPricePerNightKMF] = useState(null);
  const [loadingReserve, setLoadingReserve] = useState(false);
  const [isUserReady,    setIsUserReady]    = useState(false);
  const [agreed,         setAgreed]         = useState(false);

  const [guest, setGuest] = useState({
    firstName: "", lastName: "", email: "", phone: ""
  });

  const nights = checkIn && checkOut
    ? Math.ceil((new Date(checkOut) - new Date(checkIn)) / 86400000)
    : 0;

  // Label voyageurs
  const guestsLabel = [
    adults > 0   ? `${adults} adulte${adults > 1 ? "s" : ""}`     : null,
    children > 0 ? `${children} enfant${children > 1 ? "s" : ""}` : null,
  ].filter(Boolean).join(" · ");

  /* ── Fetch user ── */
  useEffect(() => {
    if (!user) { setIsUserReady(true); return; }
    axios.get(`${API}/api/user/${user._id}`, { withCredentials: true })
      .then(r => {
        setGuest({ firstName: r.data.name || "", lastName: r.data.surname || "", email: r.data.email || "", phone: r.data.phone || "" });
        setIsUserReady(true);
      })
      .catch(() => setIsUserReady(true));
  }, [user]);

  /* ── Fetch room ── */
  useEffect(() => {
    if (!roomId) return;
    axios.get(`${API}/api/rooms/${roomId}`).then(r => setRoom(r.data)).catch(() => {});
  }, [roomId]);

  /* ── Fetch prix + rate plan ── */
  useEffect(() => {
    if (!roomId || !ratePlanId || !checkIn || !checkOut) return;
    Promise.all([
      // ✅ Passer adults/children au calcul du prix
      axios.get(`${API}/api/rateprices/calculate`, {
        params: { roomId, ratePlanId, checkIn, checkOut, adults, children }
      }),
      axios.get(`${API}/api/rateplans/single/${ratePlanId}`, { withCredentials: true }),
    ]).then(([priceRes, planRes]) => {
      setTotal(priceRes.data.total);
      setTotalKMF(priceRes.data.totalKMF);
      setPricePerNight(priceRes.data.pricePerNight);
      setPricePerNightKMF(priceRes.data.pricePerNightKMF);
      setRatePlan(planRes.data);
    }).catch(() => {});
  }, [roomId, ratePlanId, checkIn, checkOut, adults, children]);

  /* ── Politique annulation ── */
  const getPolicyInfo = () => {
    if (!ratePlan) return null;
    const { type, hoursBeforeArrival, penaltyPercentage } = ratePlan.cancellationPolicy;
    const hoursLeft = (new Date(checkIn) - new Date()) / 3600000;
    if (type === "free") return {
      icon: <ShieldCheck size={16} color={G} />,
      label: `Annulation gratuite jusqu'à ${hoursBeforeArrival}h avant`,
      sub: hoursLeft >= hoursBeforeArrival
        ? "Remboursement total si annulation maintenant"
        : "Délai d'annulation gratuite dépassé",
      color: hoursLeft >= hoursBeforeArrival ? G : "#ef4444",
      bg:    hoursLeft >= hoursBeforeArrival ? "#f0fdf4" : "#fef2f2",
    };
    if (type === "partial") return {
      icon:  <Shield size={16} color="#f59e0b" />,
      label: `Annulation partielle — ${penaltyPercentage}% de frais`,
      sub:   `Remboursement ${100 - penaltyPercentage}% selon conditions`,
      color: "#d97706", bg: "#fffbeb",
    };
    return {
      icon:  <ShieldX size={16} color="#ef4444" />,
      label: "Non remboursable",
      sub:   "Aucun remboursement en cas d'annulation",
      color: "#ef4444", bg: "#fef2f2",
    };
  };

  const policy = getPolicyInfo();

  const MEAL_LABELS = {
    breakfast:  { emoji: "🍳",  label: "Petit-déjeuner inclus" },
    half_board: { emoji: "🍽️", label: "Demi-pension"           },
    full_board: { emoji: "🍽️", label: "Pension complète"       },
  };

  /* ── Submit ── */
  const handleReserve = async () => {
    if (!guest.email || !guest.firstName || !guest.lastName) {
      toast.error("Veuillez remplir tous les champs obligatoires"); return;
    }
    if (!guest.phone || !guest.phone.startsWith("+")) {
      toast.error("Numéro de téléphone invalide"); return;
    }
    if (!agreed) {
      toast.error("Veuillez accepter les conditions"); return;
    }
    try {
      setLoadingReserve(true);
      const res = await axios.post(`${API}/api/reservation`,
        {
          roomId,
          ratePlanId,
          checkInDate:  checkIn,
          checkOutDate: checkOut,
          // ✅ Envoyer guests dans le body
          guests: { adults, children },
          guest,
        },
        { withCredentials: true }
      );
      const resa = res.data;
      if (resa.status === "pending") navigate("/reservation-pending", { state: { email: resa.guest.email } });
      else navigate("/confirmation/" + resa._id);
    } catch (err) {
      toast.error(err.response?.data?.error || "Une erreur est survenue");
    } finally {
      setLoadingReserve(false);
    }
  };

  const isLoading = !room || !ratePlan || !isUserReady;

  return (
    <>
      <Header />
      <div style={{ minHeight: "100vh", background: "#f9fafb", fontFamily: "'DM Sans','Inter',system-ui,sans-serif", paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 960, margin: "0 auto", padding: "0 24px" }}>

          {/* ── Breadcrumb ── */}
          <button onClick={() => navigate(-1)}
            style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: 13, fontWeight: 500, marginBottom: 24, padding: 0 }}>
            <ChevronLeft size={16} /> Retour
          </button>

          <h1 style={{ fontFamily: "Georgia, serif", fontSize: 26, fontWeight: 700, color: "#111827", marginBottom: 4 }}>
            Finaliser la réservation
          </h1>
          <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 32 }}>Vérifiez les détails avant de confirmer</p>

          {/* ── Étapes ── */}
          <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 36 }}>
            {["Votre séjour", "Informations", "Confirmation"].map((step, i) => (
              <React.Fragment key={i}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%",
                    background: i === 0 ? G : i === 1 ? G : "#f3f4f6",
                    color: i < 2 ? "#fff" : "#9ca3af",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 700, flexShrink: 0,
                  }}>
                    {i < 1 ? <Check size={13} /> : i + 1}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: i < 2 ? 600 : 400, color: i < 2 ? "#111827" : "#9ca3af", whiteSpace: "nowrap" }}>{step}</span>
                </div>
                {i < 2 && <div style={{ flex: 1, height: 1, background: i < 1 ? G : "#e5e7eb", margin: "0 10px", minWidth: 20 }} />}
              </React.Fragment>
            ))}
          </div>

          {isLoading ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              {[...Array(4)].map((_, i) => (
                <div key={i} style={{ height: i % 2 === 0 ? 180 : 320, borderRadius: 16, background: "linear-gradient(90deg,#f3f4f6 25%,#e9eaec 50%,#f3f4f6 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
              ))}
              <style>{`@keyframes shimmer{to{background-position:-200% 0}}`}</style>
            </div>
          ) : (
            <div className="checkout-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, alignItems: "start" }}>

              {/* ─── COLONNE GAUCHE — Résumé ─── */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                {/* Card chambre */}
                <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #f3f4f6", padding: "20px", boxShadow: "0 2px 10px rgba(0,0,0,0.04)" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>Votre séjour</div>

                  {room && (
                    <div style={{ display: "flex", gap: 14 }}>
                      <div style={{ width: 80, height: 80, borderRadius: 12, overflow: "hidden", flexShrink: 0, background: "#f3f4f6" }}>
                        {room.photos?.[0] && <img src={room.photos[0]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 8 }}>{room.title}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6b7280", marginBottom: 5 }}>
                          <Calendar size={13} color="#9ca3af" />
                          <span>{fmtShort(checkIn)} → {fmtShort(checkOut)}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6b7280", marginBottom: 5 }}>
                          <Moon size={13} color="#9ca3af" />
                          <span>{nights} nuit{nights > 1 ? "s" : ""}</span>
                        </div>
                        {/* ✅ Voyageurs */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6b7280" }}>
                          <Users size={13} color="#9ca3af" />
                          <span>{guestsLabel}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Dates détaillées */}
                  <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    {[{ label: "Arrivée", val: fmtDate(checkIn) }, { label: "Départ", val: fmtDate(checkOut) }].map((d, i) => (
                      <div key={i} style={{ background: "#f9fafb", borderRadius: 10, padding: "10px 12px" }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 3 }}>{d.label}</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#111827" }}>{d.val}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Card tarif */}
                {ratePlan && (
                  <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #f3f4f6", padding: "20px", boxShadow: "0 2px 10px rgba(0,0,0,0.04)" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>Tarif sélectionné</div>

                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 4 }}>{ratePlan.name}</div>
                        {ratePlan.mealPlan && ratePlan.mealPlan !== "none" && MEAL_LABELS[ratePlan.mealPlan] && (
                          <div style={{ fontSize: 12, color: "#6b7280" }}>
                            {MEAL_LABELS[ratePlan.mealPlan].emoji} {MEAL_LABELS[ratePlan.mealPlan].label}
                          </div>
                        )}
                        {/* ✅ Supplément voyageurs si applicable */}
                        {(ratePlan.extraGuestPolicy?.extraAdultFee > 0 || ratePlan.extraGuestPolicy?.extraChildFee > 0) && (
                          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                            {adults > (ratePlan.extraGuestPolicy?.baseOccupancy || 1) && (
                              <span>+{ratePlan.extraGuestPolicy.extraAdultFee}€/adulte sup. · </span>
                            )}
                            {children > 0 && ratePlan.extraGuestPolicy?.extraChildFee > 0 && (
                              <span>+{ratePlan.extraGuestPolicy.extraChildFee}€/enfant</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: "#111827", lineHeight: 1 }}>{pricePerNight}€</div>
                        <div style={{ fontSize: 11, color: "#9ca3af" }}>/ nuit</div>
                        {/* ✅ KMF par nuit */}
                        {pricePerNightKMF && (
                          <div style={{ fontSize: 10, color: "#9ca3af" }}>{pricePerNightKMF?.toLocaleString("fr-FR")} KMF</div>
                        )}
                      </div>
                    </div>

                    {/* Politique */}
                    {policy && (
                      <div style={{ background: policy.bg, borderRadius: 10, padding: "10px 12px", display: "flex", alignItems: "flex-start", gap: 10 }}>
                        {policy.icon}
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: policy.color }}>{policy.label}</div>
                          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{policy.sub}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Card total */}
                <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #f3f4f6", padding: "20px", boxShadow: "0 2px 10px rgba(0,0,0,0.04)" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>Récapitulatif</div>

                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#6b7280", marginBottom: 8 }}>
                    <span>{pricePerNight}€ × {nights} nuit{nights > 1 ? "s" : ""}</span>
                    <span>{total ? `${total}€` : "..."}</span>
                  </div>
                  {/* ✅ Ligne voyageurs si supplément */}
                  {guestsLabel && (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>
                      <span>{guestsLabel}</span>
                      <span>Inclus</span>
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#9ca3af", marginBottom: 14 }}>
                    <span>Taxes et frais</span>
                    <span>Inclus</span>
                  </div>

                  <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 14, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Total</span>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontFamily: "Georgia, serif", fontSize: 26, fontWeight: 700, color: "#111827" }}>
                        {total || "..."}€
                      </div>
                      {/* ✅ KMF total */}
                      {totalKMF && (
                        <div style={{ fontSize: 12, color: "#9ca3af" }}>
                          ≈ {totalKMF?.toLocaleString("fr-FR")} KMF
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#9ca3af" }}>
                    <Info size={12} color="#9ca3af" />
                    Aucun paiement prélevé aujourd'hui
                  </div>
                </div>
              </div>

              {/* ─── COLONNE DROITE — Formulaire ─── */}
              <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #f3f4f6", padding: "24px", boxShadow: "0 2px 10px rgba(0,0,0,0.04)" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 20 }}>Informations client</div>

                {!isUserReady ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {[...Array(4)].map((_, i) => (
                      <div key={i} style={{ height: 44, borderRadius: 10, background: "#f3f4f6", animation: "shimmer 1.4s infinite" }} />
                    ))}
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

                    {user && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#f0fdf4", borderRadius: 10, padding: "8px 12px", marginBottom: 4 }}>
                        <Check size={13} color={G} />
                        <span style={{ fontSize: 12, color: G, fontWeight: 500 }}>Informations pré-remplies depuis votre compte</span>
                      </div>
                    )}

                    {[
                      { key: "lastName",  placeholder: "Nom *",    type: "text"  },
                      { key: "firstName", placeholder: "Prénom *",  type: "text"  },
                      { key: "email",     placeholder: "Email *",   type: "email" },
                    ].map(f => (
                      <div key={f.key} style={{ position: "relative" }}>
                        <input
                          type={f.type}
                          value={guest[f.key]}
                          onChange={e => setGuest({ ...guest, [f.key]: e.target.value })}
                          placeholder={f.placeholder}
                          style={{
                            width: "100%", border: "1px solid #e5e7eb", borderRadius: 10,
                            padding: "11px 14px", fontSize: 13, outline: "none",
                            fontFamily: "inherit", color: "#111827",
                            boxSizing: "border-box", transition: "border-color 0.18s",
                          }}
                          onFocus={e => e.target.style.borderColor = G}
                          onBlur={e => e.target.style.borderColor = "#e5e7eb"}
                        />
                      </div>
                    ))}

                    <PhoneInput
                      country="km"
                      value={guest.phone?.replace("+", "")}
                      onChange={phone => setGuest(p => ({ ...p, phone: "+" + phone }))}
                      inputStyle={{ width: "100%", height: 44, borderRadius: 10, border: "1px solid #e5e7eb", fontSize: 13, fontFamily: "inherit" }}
                      buttonStyle={{ borderTopLeftRadius: 10, borderBottomLeftRadius: 10, border: "1px solid #e5e7eb", borderRight: "none" }}
                    />

                    <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", marginTop: 4 }}>
                      <div
                        onClick={() => setAgreed(!agreed)}
                        style={{
                          width: 18, height: 18, borderRadius: 5, border: `2px solid ${agreed ? G : "#d1d5db"}`,
                          background: agreed ? G : "#fff", flexShrink: 0, marginTop: 1,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          transition: "all 0.15s", cursor: "pointer",
                        }}>
                        {agreed && <Check size={11} color="#fff" strokeWidth={3} />}
                      </div>
                      <span style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>
                        J'accepte les{" "}
                        <a href="/conditions" target="_blank" style={{ color: G, fontWeight: 600, textDecoration: "none" }}>conditions générales</a>
                        {" "}et la politique d'annulation du tarif sélectionné.
                      </span>
                    </label>

                    <button
                      onClick={handleReserve}
                      disabled={loadingReserve}
                      style={{
                        marginTop: 8, width: "100%", background: loadingReserve ? "#d1d5db" : G,
                        color: "#fff", border: "none", borderRadius: 12,
                        padding: "14px 0", fontSize: 14, fontWeight: 700,
                        cursor: loadingReserve ? "not-allowed" : "pointer",
                        transition: "background 0.18s",
                      }}
                      onMouseEnter={e => { if (!loadingReserve) e.currentTarget.style.backgroundColor = G_DARK; }}
                      onMouseLeave={e => { if (!loadingReserve) e.currentTarget.style.backgroundColor = G; }}>
                      {loadingReserve ? "Réservation en cours..." : "Confirmer la réservation →"}
                    </button>

                    <p style={{ fontSize: 11, textAlign: "center", color: "#9ca3af", marginTop: 4 }}>
                      En confirmant, vous acceptez les conditions du tarif sélectionné.
                    </p>
                  </div>
                )}
              </div>

            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes shimmer { to { background-position: -200% 0; } }
        @media (max-width: 768px) {
          .checkout-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );
}